/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

export interface PanelContentProps {
	/**
	 * The scrollable content of the panel — typically a `PanelSubheader` followed by a `PanelBody`.
	 */
	children: ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	content: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
		overflow: 'auto',
	},
});

/**
 * The PanelContent component is the scrollable region of a panel, sitting between the fixed
 * `PanelHeader` and `PanelFooter`. Wrap the `PanelSubheader` and `PanelBody` in it so they scroll
 * together while the header and footer stay fixed.
 *
 * It applies no padding of its own, so the `PanelSubheader` cover image can bleed to the panel
 * edges and the `PanelBody` keeps its own padding.
 */
export function PanelContent({ children, testId }: PanelContentProps): JSX.Element {
	return (
		<Box testId={testId} xcss={styles.content}>
			{children}
		</Box>
	);
}
